import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, CATEGORY_META } from "../api";
import { LogOut, Loader2, Search, X, RefreshCw, BadgeCheck, User, Sparkles } from "lucide-react";

const authFetchBlob = async (path, token) => {
  const { data } = await axios.get(`${API}/admin/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
  return URL.createObjectURL(data);
};

const ProAvatar = ({ token, path }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    if (path) authFetchBlob(path, token).then((x) => { u = x; setUrl(x); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [path, token]);
  if (!path) return <div className="w-10 h-10 rounded-full bg-cream-deep flex items-center justify-center text-ink-faint"><User size={16} /></div>;
  return url ? <img src={url} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-cream-deep animate-pulse" />;
};

const CvLink = ({ token, path }) => {
  const open = async () => { try { const url = await authFetchBlob(path, token); window.open(url, "_blank"); } catch (e) {} };
  if (!path) return <span className="text-xs text-ink-faint">—</span>;
  return <button onClick={open} className="text-xs text-petrol font-semibold underline" data-testid="cv-link">Ver CV</button>;
};

const REQUEST_STATUSES = ["NUEVA", "CONTACTADA", "EN BÚSQUEDA", "PROFESIONAL ASIGNADO", "SERVICIO CONFIRMADO", "SERVICIO REALIZADO", "COMPLETADA", "CANCELADA"];
const PRO_STATUSES = ["PENDIENTE", "CONTACTADO", "EN VERIFICACIÓN", "APROBADO", "NO APROBADO", "INACTIVO"];
const CAT_LABEL = { hogar: "Hogar", mascotas: "Mascotas", acompanamiento: "Acompañamiento", recados: "Recados" };

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const StatusPill = ({ value }) => {
  const color = value === "NUEVA" ? "bg-natural/15 text-natural" : value === "APROBADO" || value === "COMPLETADA" ? "bg-forest/15 text-forest" : value === "CANCELADA" || value === "NO APROBADO" ? "bg-red-100 text-red-600" : value === "PENDIENTE" ? "bg-beige/60 text-olive" : "bg-cream-deep text-ink-soft";
  return <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${color}`}>{value}</span>;
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("hh_token", data.access_token);
      onLogin(data.user);
    } catch (err) {
      setLoading(false);
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Error de acceso");
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-3xl border border-line shadow-soft p-8">
        <div className="flex items-center gap-2 font-heading font-extrabold text-xl text-ink"><span className="w-2.5 h-2.5 rounded-full bg-forest" /> HomeHealthy</div>
        <p className="mt-1 text-sm text-ink-faint">Panel de administración</p>
        <div className="mt-6 space-y-4">
          <div><label className="block text-sm font-medium text-ink-soft mb-1.5">Email</label><input className="w-full rounded-xl border border-line bg-cream/50 px-4 py-3 outline-none focus:border-forest" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" /></div>
          <div><label className="block text-sm font-medium text-ink-soft mb-1.5">Contraseña</label><input type="password" className="w-full rounded-xl border border-line bg-cream/50 px-4 py-3 outline-none focus:border-forest" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" /></div>
          {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
          <button disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-forest text-white font-semibold px-6 py-3.5 hover:bg-sage-deep transition-all disabled:opacity-60" data-testid="admin-login-submit">
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Entrar"}
          </button>
        </div>
      </form>
    </div>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-line p-5" data-testid={testid}>
    <div className="text-3xl font-heading font-bold text-forest">{value ?? "—"}</div>
    <div className="mt-1 text-sm text-ink-soft">{label}</div>
  </div>
);

const MatchPanel = ({ token, request, onClose, onAssigned }) => {
  const [pros, setPros] = useState([]);
  const [postal, setPostal] = useState(request.postal_code || "");
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ status: "APROBADO", category: request.category });
    if (postal) params.append("postal_code", postal);
    const { data } = await axios.get(`${API}/admin/professionals?${params}`, authHeaders(token));
    setPros(data); setLoading(false);
  }, [token, request.category, postal]);
  useEffect(() => { load(); }, [load]);
  const [sug, setSug] = useState(null);
  const [sugLoading, setSugLoading] = useState(false);
  const aiSuggest = async () => {
    setSugLoading(true);
    try { const { data } = await axios.post(`${API}/admin/requests/${request.id}/ai-suggest`, {}, authHeaders(token)); setSug(data); }
    catch (e) { setSug({ summary: "Asistente IA no disponible.", suggested_id: null }); }
    setSugLoading(false);
  };
  const assign = async (proId) => {
    await axios.patch(`${API}/admin/requests/${request.id}/assign`, { professional_id: proId }, authHeaders(token));
    onAssigned();
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" data-testid="match-panel">
      <div className="absolute inset-0 bg-sage-deep/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-white rounded-3xl shadow-2xl p-6 md:p-8">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream border border-line flex items-center justify-center" data-testid="match-close"><X size={18} /></button>
        <h3 className="font-heading text-xl font-medium text-ink">Profesionales disponibles</h3>
        <p className="text-sm text-ink-soft mt-1">Solicitud {request.ref} · {CAT_LABEL[request.category]} · {request.postal_code} {request.neighborhood}</p>
        <button onClick={aiSuggest} disabled={sugLoading} className="mt-3 inline-flex items-center gap-2 rounded-full border border-petrol text-petrol font-semibold px-4 py-2 text-sm hover:bg-petrol hover:text-white transition-all disabled:opacity-50" data-testid="ai-suggest-btn">
          {sugLoading ? <><Loader2 size={15} className="animate-spin" /> Analizando…</> : <><Sparkles size={15} /> Sugerencia IA</>}
        </button>
        {sug && (
          <div className="mt-3 rounded-2xl bg-cream border border-line p-4 text-sm" data-testid="ai-suggest-result">
            {sug.summary && <p className="text-ink"><span className="font-semibold">Resumen:</span> {sug.summary}</p>}
            {sug.suggested_name && <p className="mt-1 text-ink"><span className="font-semibold">Sugerido:</span> {sug.suggested_name}{sug.reason ? ` — ${sug.reason}` : ""}</p>}
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <input placeholder="Código postal" className="rounded-xl border border-line bg-cream/50 px-4 py-2.5 outline-none focus:border-forest" value={postal} onChange={(e) => setPostal(e.target.value)} data-testid="match-postal" />
          <button onClick={load} className="inline-flex items-center gap-2 rounded-xl bg-forest text-white px-4 py-2.5 font-semibold"><Search size={16} /> Filtrar</button>
        </div>
        <div className="mt-4 space-y-3">
          {loading ? <div className="py-8 text-center text-ink-faint"><Loader2 className="animate-spin inline" /></div> :
            pros.length === 0 ? <p className="py-8 text-center text-ink-faint">No hay profesionales aprobados que coincidan.</p> :
            pros.map((p) => (
              <div key={p.id} className={`flex items-center justify-between gap-4 rounded-2xl border p-4 ${sug?.suggested_id === p.id ? "border-petrol ring-2 ring-petrol/20 bg-cream" : "border-line"}`} data-testid={`match-pro-${p.id}`}>
                <div>
                  <div className="font-medium text-ink flex items-center gap-2">{p.name} {p.last_name}{sug?.suggested_id === p.id && <span className="inline-flex items-center gap-1 text-xs font-semibold text-petrol"><Sparkles size={12} /> IA</span>}</div>
                  <div className="text-sm text-ink-faint">{(p.categories || []).map((c) => CAT_LABEL[c]).join(", ")} · {p.postal_code} · {p.hourly_rate}€/h</div>
                </div>
                <button onClick={() => assign(p.id)} className="rounded-full bg-forest text-white font-semibold px-4 py-2 text-sm hover:bg-sage-deep" data-testid={`assign-${p.id}`}>Asignar</button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ token, user, onLogout }) => {
  const [tab, setTab] = useState("requests");
  const [stats, setStats] = useState({});
  const [requests, setRequests] = useState([]);
  const [pros, setPros] = useState([]);
  const [match, setMatch] = useState(null);
  const [proMap, setProMap] = useState({});

  const load = useCallback(async () => {
    const [s, r, p] = await Promise.all([
      axios.get(`${API}/admin/stats`, authHeaders(token)),
      axios.get(`${API}/admin/requests`, authHeaders(token)),
      axios.get(`${API}/admin/professionals`, authHeaders(token)),
    ]);
    setStats(s.data); setRequests(r.data); setPros(p.data);
    const map = {}; p.data.forEach((x) => { map[x.id] = `${x.name} ${x.last_name}`; });
    setProMap(map);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setReqStatus = async (id, status) => { await axios.patch(`${API}/admin/requests/${id}/status`, { status }, authHeaders(token)); load(); };
  const setProStatus = async (id, status) => { await axios.patch(`${API}/admin/professionals/${id}/status`, { status }, authHeaders(token)); load(); };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-white border-b border-line sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-extrabold text-lg text-ink"><span className="w-2.5 h-2.5 rounded-full bg-forest" /> HomeHealthy <span className="text-ink-faint font-normal text-sm">· Admin</span></div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink" data-testid="admin-refresh"><RefreshCw size={15} /> Actualizar</button>
            <button onClick={onLogout} className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-cream" data-testid="admin-logout"><LogOut size={15} /> Salir</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Solicitudes nuevas" value={stats.requests_new} testid="stat-new" />
          <StatCard label="En proceso" value={stats.requests_in_progress} testid="stat-progress" />
          <StatCard label="Profesionales" value={stats.professionals_total} testid="stat-pros" />
          <StatCard label="Pendientes revisión" value={stats.professionals_pending} testid="stat-pending" />
          <StatCard label="Servicios realizados" value={stats.services_done} />
          <StatCard label="Valoraciones" value={stats.reviews_total} />
          <StatCard label="Clics 'Necesito ayuda'" value={stats.cta_help_clicks} />
          <StatCard label="Solicitudes completadas" value={stats.forms_completed} />
        </div>

        <div className="mt-8 flex gap-2">
          <button onClick={() => setTab("requests")} className={`rounded-full px-5 py-2.5 text-sm font-semibold ${tab === "requests" ? "bg-forest text-white" : "bg-white border border-line text-ink-soft"}`} data-testid="tab-requests">Solicitudes</button>
          <button onClick={() => setTab("pros")} className={`rounded-full px-5 py-2.5 text-sm font-semibold ${tab === "pros" ? "bg-forest text-white" : "bg-white border border-line text-ink-soft"}`} data-testid="tab-pros">Profesionales</button>
        </div>

        {tab === "requests" ? (
          <div className="mt-4 bg-white rounded-2xl border border-line overflow-x-auto" data-testid="requests-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-ink-faint border-b border-line">
                <th className="p-4">Ref</th><th className="p-4">Cliente</th><th className="p-4">Categoría</th><th className="p-4">Zona</th><th className="p-4">Cuándo</th><th className="p-4">Estado</th><th className="p-4">Profesional</th><th className="p-4"></th>
              </tr></thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-b border-line/60" data-testid={`req-row-${r.ref}`}>
                    <td className="p-4 font-semibold text-forest">{r.ref}</td>
                    <td className="p-4"><div className="font-medium text-ink">{r.name}</div><div className="text-ink-faint">{r.phone}</div>{r.ai_summary && <div className="mt-1 flex items-start gap-1 text-xs text-petrol italic max-w-[220px]"><Sparkles size={12} className="mt-0.5 shrink-0" /> {r.ai_summary}</div>}</td>
                    <td className="p-4">{CAT_LABEL[r.category] || r.category}</td>
                    <td className="p-4">{r.postal_code} {r.neighborhood}</td>
                    <td className="p-4">{r.requested_date} {r.time_range}</td>
                    <td className="p-4"><select value={r.status} onChange={(e) => setReqStatus(r.id, e.target.value)} className="rounded-lg border border-line bg-cream/50 px-2 py-1.5 text-xs" data-testid={`req-status-${r.ref}`}>{REQUEST_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></td>
                    <td className="p-4 text-ink-soft">{r.assigned_professional_id ? proMap[r.assigned_professional_id] || "Asignado" : "—"}</td>
                    <td className="p-4"><button onClick={() => setMatch(r)} className="inline-flex items-center gap-1.5 rounded-full bg-petrol text-white px-3 py-1.5 text-xs font-semibold" data-testid={`match-open-${r.ref}`}><Search size={13} /> Buscar</button></td>
                  </tr>
                ))}
                {requests.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-ink-faint">Sin solicitudes todavía.</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4 bg-white rounded-2xl border border-line overflow-x-auto" data-testid="pros-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-ink-faint border-b border-line">
                <th className="p-4">Perfil</th><th className="p-4">Nombre</th><th className="p-4">Zona</th><th className="p-4">Categorías</th><th className="p-4">Exp.</th><th className="p-4">Disponibilidad</th><th className="p-4">€/h</th><th className="p-4">Estado</th>
              </tr></thead>
              <tbody>
                {pros.map((p) => (
                  <tr key={p.id} className="border-b border-line/60" data-testid={`pro-row-${p.id}`}>
                    <td className="p-4"><div className="flex flex-col items-center gap-1"><ProAvatar token={token} path={p.photo} /><CvLink token={token} path={p.cv} /></div></td>
                    <td className="p-4">
                      <div className="font-medium text-ink flex items-center gap-1.5">{p.name} {p.last_name}
                        {p.verification_status === "APROBADO" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-forest" data-testid={`verified-${p.id}`}><BadgeCheck size={15} /> Verificado</span>}
                      </div>
                      <div className="text-ink-faint">{p.email}</div>
                    </td>
                    <td className="p-4">{p.postal_code}</td>
                    <td className="p-4">{(p.categories || []).map((c) => CAT_LABEL[c]).join(", ")}</td>
                    <td className="p-4">{p.experience || "—"}</td>
                    <td className="p-4">{p.availability || "—"}</td>
                    <td className="p-4">{p.hourly_rate || "—"}</td>
                    <td className="p-4"><select value={p.verification_status} onChange={(e) => setProStatus(p.id, e.target.value)} className="rounded-lg border border-line bg-cream/50 px-2 py-1.5 text-xs" data-testid={`pro-status-${p.id}`}>{PRO_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></td>
                  </tr>
                ))}
                {pros.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-ink-faint">Sin profesionales todavía.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {match && <MatchPanel token={token} request={match} onClose={() => setMatch(null)} onAssigned={() => { setMatch(null); load(); }} />}
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const token = typeof window !== "undefined" ? localStorage.getItem("hh_token") : null;

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, authHeaders(token))
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem("hh_token"))
      .finally(() => setChecking(false));
  }, [token]);

  const logout = () => { localStorage.removeItem("hh_token"); setUser(null); };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-cream"><Loader2 className="animate-spin text-forest" /></div>;
  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard token={localStorage.getItem("hh_token")} user={user} onLogout={logout} />;
}
